/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";

import showErrMessage from "@components/ShowErrMessage";

import { getQuestion, sendAnswer } from "./competition.api";
import DisclaimerView from "./DisclaimerView";
import {
  RESULT_ICONS,
  errorMessage,
  timeWithSuffix,
} from "./competition.constants";
import ResultCard from "./ResultCard";

/** Süre bittikten sonra sonucu sormadan önce beklenen süre (sunucudaki tolerans + ağ payı). */
const TIMEOUT_SETTLE_MS = 4000;

const formatClock = (seconds) => {
  const safe = Math.max(0, Math.ceil(seconds));
  return `${String(Math.floor(safe / 60)).padStart(2, "0")}:${String(safe % 60).padStart(2, "0")}`;
};

/**
 * Yarışma kartının içi: başlangıç, soru, sonuç. Soru yalnızca kullanıcı "Yarışmaya Başla"
 * dediğinde istenir; çünkü soru istemek 90 saniyelik süreyi başlatır. Gün zaten bitmişse
 * (DONE) kullanıcı yine ilk girişteki gibi şart ve koşulları onaylar (yoksa "Yarışmaya Başla"
 * der); ardından sonuç, süre başlatmayan aynı uçtan alınır. Onay yeniden kaydedilmez.
 */
const PlayArea = ({ competition, onDayChanged }) => {
  const [question, setQuestion] = useState(null);
  const [result, setResult] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  // Yalnızca açılışta DONE ise: bu oturumda yanıtlayan kullanıcı sonucunu görmeye devam eder.
  const [revisitGate, setRevisitGate] = useState(
    competition.todayState === "DONE"
  );
  const [agreed, setAgreed] = useState(false);

  const rightsFull =
    (competition.winCount || 0) >= (competition.maxWinCount || 1);
  // Finalli yarışmada hakkını dolduran da finale kalmak için oynamaya devam eder.
  const wonAll =
    rightsFull &&
    !competition.finalProgress &&
    competition.todayState !== "DONE";

  const apply = (dto) => {
    setQuestion(null);
    setResult(null);
    setInfo(null);
    switch (dto?.state) {
      case "QUESTION":
        setQuestion({ ...dto, receivedAt: Date.now() });
        break;
      case "DONE":
        setResult(dto.result || { kind: "CORRECT" });
        onDayChanged?.();
        break;
      case "NOT_STARTED":
        setInfo({
          message: `Yarışmamız bugün saat ${timeWithSuffix(dto.opensAt)} başlayacaktır.`,
          imageUrl: RESULT_ICONS.time,
        });
        break;
      case "NO_QUESTION":
        setInfo({
          message:
            "Bugün için soru bulunmuyor. Yarın yeni soruyla görüşmek üzere!",
        });
        break;
      default:
        setInfo({
          message:
            "Bugünün yarışması sona erdi. Yeni soru yarın sizi bekliyor olacak.",
        });
    }
  };

  const load = () => {
    setLoading(true);
    return getQuestion(competition.id)
      .then((res) => apply(res.data))
      .catch((error) => showErrMessage(errorMessage(error)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setRevisitGate(competition.todayState === "DONE");
    setAgreed(false);
  }, [competition.id]);

  const passRevisitGate = () => {
    if (competition.disclaimer && !agreed) {
      showErrMessage("Devam etmek için şart ve koşulları onaylayınız.");
      return;
    }
    setRevisitGate(false);
    load();
  };

  if (loading && !question) {
    return (
      <div className="cmp-card-body cmp-center">
        <div className="cmp-spinner" />
      </div>
    );
  }

  if (revisitGate) {
    return competition.disclaimer ? (
      <DisclaimerView
        text={competition.disclaimer}
        agreed={agreed}
        onAgreedChange={setAgreed}
        onSubmit={passRevisitGate}
      />
    ) : (
      <StartView
        competition={{ ...competition, todayState: "OPEN" }}
        onStart={passRevisitGate}
      />
    );
  }

  if (question) {
    return (
      <QuestionView
        competitionId={competition.id}
        question={question}
        onResult={(next) => {
          setQuestion(null);
          setResult(next);
          if (next?.kind !== "CORRECT_NEXT") onDayChanged?.();
        }}
        onReload={load}
      />
    );
  }

  if (result) {
    return (
      <ResultCard
        result={result}
        unitName={competition.finalProgress?.unitName}
        finalStartDate={competition.finalProgress?.finalStartDate}
        onNext={result.kind === "CORRECT_NEXT" ? load : null}
      />
    );
  }

  if (info) {
    return (
      <div className="cmp-card-body cmp-center">
        {info.imageUrl && (
          <img className="cmp-result-image" src={info.imageUrl} alt="" />
        )}
        <p className="cmp-info-text">{info.message}</p>
      </div>
    );
  }

  return (
    <StartView
      competition={competition}
      wonAll={wonAll}
      keepPlayingForFinal={rightsFull && Boolean(competition.finalProgress)}
      onStart={load}
    />
  );
};

const StartView = ({ competition, wonAll, keepPlayingForFinal, onStart }) => {
  const state = competition.todayState;
  let body;

  if (wonAll) {
    body = (
      <p className="cmp-info-text">
        Bu yarışmadaki hediye kazanma hakkınızı doldurdunuz. Şart ve koşullarda
        belirtildiği gibi yarışma süresince sorulara erişemez ve yeniden hediye
        kazanamazsınız. Hediyenizi iyi günlerde kullanmanızı dileriz.
      </p>
    );
  } else if (state === "OPEN") {
    body = (
      <>
        {keepPlayingForFinal ? (
          <p className="cmp-info-text">
            Bu yarışmadaki hediye kazanma hakkınızı doldurdunuz; yeni hediye
            kazanamazsınız. Finale katılabilmek için soruları yanıtlamaya devam
            edebilirsiniz. Her soru için <strong>90 saniye</strong> süreniz
            olacak.
          </p>
        ) : (
          <p className="cmp-info-text">
            Bugünün sorusu sizi bekliyor! Başladığınızda her soru için{" "}
            <strong>90 saniye</strong> süreniz olacak.
          </p>
        )}
        <button
          type="button"
          className="cmp-btn cmp-btn-primary cmp-btn-lg"
          onClick={onStart}
        >
          Yarışmaya Başla
        </button>
      </>
    );
  } else if (state === "NOT_STARTED") {
    body = (
      <p className="cmp-info-text">
        Yarışmamız{" "}
        {competition.todayOpensAt
          ? `bugün saat ${timeWithSuffix(competition.todayOpensAt)}`
          : "yakında"}{" "}
        başlayacaktır.
      </p>
    );
  } else if (state === "NO_QUESTION") {
    body = (
      <p className="cmp-info-text">
        Bugün için soru bulunmuyor. Yarın yeni soruyla görüşmek üzere!
      </p>
    );
  } else {
    body = (
      <p className="cmp-info-text">
        Bugünün yarışması sona erdi. Kazananlar ve doğru yanıtlayanlar yarın
        yayınlanacak.
      </p>
    );
  }

  return <div className="cmp-card-body cmp-center">{body}</div>;
};

const QuestionView = ({ competitionId, question, onResult, onReload }) => {
  const [choiceId, setChoiceId] = useState(null);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const total = 90;
  const deadline = useRef(
    question.receivedAt + (question.remainingSeconds || 0) * 1000
  );
  const [left, setLeft] = useState(question.remainingSeconds || 0);

  const expired = left <= 0;
  const isText = question.questionType === "FILL_IN_BLANK";
  const isImage = question.questionType === "IMAGE_CHOICE";
  // Soruda görsel varken kısa şıklar iki sütunda: şıklar kaydırmadan görünsün
  const compactChoices =
    !isImage &&
    question.questionImageUrl &&
    (question.choices || []).every(
      (choice) => (choice.name || "").length <= 10
    );

  useEffect(() => {
    deadline.current =
      question.receivedAt + (question.remainingSeconds || 0) * 1000;
    setChoiceId(null);
    setText("");
    setError("");
  }, [question.gameId, question.receivedAt]);

  useEffect(() => {
    const tick = () =>
      setLeft(Math.max(0, (deadline.current - Date.now()) / 1000));
    tick();
    const timer = setInterval(tick, 250);
    return () => clearInterval(timer);
  }, [question.gameId, question.receivedAt]);

  // Süre bitince sunucu cevabı süre aşımı olarak kapatır; kısa bir tolerans sonra sonucu sor.
  useEffect(() => {
    if (!expired || submitting) return undefined;
    const timer = setTimeout(onReload, TIMEOUT_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [expired, submitting]);

  const submit = () => {
    if (submitting || expired) return;
    if (isText ? !text.trim() : !choiceId) {
      setError(
        isText ? "Lütfen cevabınızı yazınız." : "Lütfen bir seçenek seçiniz."
      );
      return;
    }
    setError("");
    setSubmitting(true);
    sendAnswer(competitionId, {
      gameId: question.gameId,
      choiceId: isText ? null : choiceId,
      textAnswer: isText ? text.trim() : null,
    })
      .then((res) => onResult(res.data))
      .catch((err) => {
        showErrMessage(errorMessage(err));
        setSubmitting(false);
      });
  };

  const percent = Math.min(100, Math.max(0, (left / total) * 100));

  return (
    <div
      className={`cmp-card-body cmp-question ${
        question.questionImageUrl ? "has-image" : ""
      }`}
    >
      <div className="cmp-question-scroll">
        {question.questionCount > 1 && (
          <div className="cmp-question-order">
            Soru {question.order} / {question.questionCount}
          </div>
        )}
        <h2 className="cmp-question-title">{question.title}</h2>
        {question.description && (
          <p className="cmp-question-desc">{question.description}</p>
        )}
        {question.questionImageUrl && (
          <div className="cmp-media">
            <img
              className="cmp-question-image"
              src={question.questionImageUrl}
              alt=""
            />
          </div>
        )}

        {isText ? (
          <input
            type="text"
            className="cmp-text-answer"
            placeholder="Cevabınızı yazınız"
            value={text}
            maxLength={500}
            disabled={expired || submitting}
            onChange={(event) => {
              setText(event.target.value);
              setError("");
            }}
            onKeyDown={(event) => event.key === "Enter" && submit()}
          />
        ) : (
          <div
            className={`cmp-choices ${isImage ? "cmp-choices-image" : ""} ${
              compactChoices ? "cmp-choices-compact" : ""
            }`}
            role="radiogroup"
          >
            {(question.choices || []).map((choice) => (
              <label
                key={choice.id}
                className={`cmp-choice ${choiceId === choice.id ? "selected" : ""} ${
                  expired || submitting ? "disabled" : ""
                }`}
              >
                <input
                  type="radio"
                  name={`choice-${question.gameId}`}
                  checked={choiceId === choice.id}
                  disabled={expired || submitting}
                  onChange={() => {
                    setChoiceId(choice.id);
                    setError("");
                  }}
                />
                {isImage && choice.imageUrl && (
                  <img src={choice.imageUrl} alt={choice.name || ""} />
                )}
                <span className="cmp-choice-mark" />
                <span className="cmp-choice-text">{choice.name}</span>
              </label>
            ))}
          </div>
        )}

        {error && <div className="cmp-error">{error}</div>}
      </div>

      <div className="cmp-question-footer">
        <div className={`cmp-timer ${left <= 10 ? "warn" : ""}`}>
          {formatClock(left)}
        </div>
        <button
          type="button"
          className="cmp-btn cmp-btn-primary"
          disabled={expired || submitting}
          onClick={submit}
        >
          {submitting ? "Gönderiliyor…" : "Cevapla"}
        </button>
      </div>
      <div className="cmp-progress">
        <span style={{ width: `${percent}%` }} />
      </div>
      {expired && !submitting && (
        <p className="cmp-muted">Süre doldu, sonuç alınıyor…</p>
      )}
    </div>
  );
};

export default PlayArea;
